import { useCallback } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  datasetRows,
  fetchHroasResults,
  getHroasResultsQueryKey,
  HroasResultsBundle,
  TWOSTAGE_DECOMPOSITION_DATA_KEY,
  TWOSTAGE_DECOMPOSITION_INCLUDE
} from './hroasResults';
import { deriveSlots, tagRowsWithSlot } from './slots';
import { resolveFeatureRowMultiMap } from './featureIdResolution';
import { FeatureMapsByModelType, toFeatureMappingType } from './useFeatureMapping';

/**
 * Real reallocation-graph data for the rMMM-style Sankey (`SpendAndEffectModal`), sourced from
 * `twostage_decomposition_over_time.csv` (DEV-21662) — the purpose-built file. Wired into the modal
 * trigger and the CPTB "Actualized" bar chart.
 */

// One row of twostage_decomposition_over_time.csv, as read off the run's S3 folder. `value` may
// arrive as a numeric string depending on how the lambda serializes the dataframe.
export interface TwostageDecompositionCsvRow {
  kpi: string;
  model_type: string;
  slotId: string;
  model_scope: string;
  regional_scope: string;
  primary_feature_id: string;
  primary_name: string;
  primary_channel: string;
  primary_platform: string;
  primary_tactic: string;
  primary_brand_nonbrand: string;
  primary_media_type: string;
  secondary_feature_id: string;
  secondary_name: string;
  secondary_channel: string;
  secondary_platform: string;
  secondary_tactic: string;
  secondary_brand_nonbrand: string;
  secondary_media_type: string;
  geo: string;
  date: string;
  spend_or_effect: 'spend' | 'effect' | string;
  category_type: 'direct' | 'added' | 'reduced' | string;
  estimate_type: 'mean' | 'ci_lo' | 'ci_hi' | string;
  value: number | string;
}

// Matches roasPipeline.ts's local `const METRIC_MEAN = 'mean';` convention — each HROAS utils
// module that needs the literal declares its own copy rather than importing across files (see also
// ModelResults.tsx's module-local METRIC_MEAN, which is not exported).
const METRIC_MEAN = 'mean';

// Matches ModelResults.tsx's STAGE_TWO_TYPE_SPEND / STAGE_TWO_TYPE_EFFECT convention.
const STAGE_TWO_TYPE_SPEND = 'Spend' as const;
const STAGE_TWO_TYPE_EFFECT = 'Effect' as const;

export const TWOSTAGE_CATEGORY_DIRECT = 'direct' as const;
export const TWOSTAGE_CATEGORY_ADDED = 'added' as const;
export const TWOSTAGE_CATEGORY_REDUCED = 'reduced' as const;

const GROUP_KEY_SEPARATOR = '|||';

/**
 * Drop-in-adjacent output shape for `StageTwoFileData` (src/MMM/ModelResults/DataFormatters.tsx).
 * Field names/roles match where practical; documented deviations:
 *  - `cat` carries the raw `category_type` value (`'direct' | 'added' | 'reduced'`) instead of the
 *    single fabricated `'Direct'` literal StageTwoFileData rows use today — the whole point of this
 *    transform is that added/reduced reallocation rows now exist.
 *  - `*_feature_id` fields are new (not on StageTwoFileData) — carried through per the underlying
 *    CSV contract so a future name-resolution pass, or the Sankey itself, can key off them even
 *    when a name doesn't resolve.
 *  - `refresh_date` is dropped — twostage_decomposition_over_time.csv carries no such column.
 */
export interface TwostageDecompositionRow {
  date: string;
  primary_channel: string;
  primary_channel_feature_id?: string;
  primary_channel_channel?: string;
  primary_channel_platform?: string;
  primary_channel_tactic?: string;
  primary_channel_brand_nonbrand?: string;
  primary_channel_media_type?: string;
  secondary_channel: string;
  secondary_channel_feature_id?: string;
  secondary_channel_channel?: string;
  secondary_channel_platform?: string;
  secondary_channel_tactic?: string;
  secondary_channel_brand_nonbrand?: string;
  secondary_channel_media_type?: string;
  value: number;
  cat: 'direct' | 'added' | 'reduced';
  type: typeof STAGE_TWO_TYPE_SPEND | typeof STAGE_TWO_TYPE_EFFECT;
  primary_channel_incrementality_test: boolean;
}

interface ResolvedSide {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
}

/**
 * Resolve one side (primary or secondary) of a row via the shared featureIdResolution adapter.
 * Feeds it a FeatureRowShape-compatible object built from the prefixed CSV columns, since the
 * resolver expects unprefixed `name`/`feature_id`/`channel`/... fields, and dispatches through the
 * per-model-type multi-map keyed on the row's own `model_type` (HROAS blends rMMM/GeoMMM/BrandEquity
 * rows in one bundle). Old-format rows (name already populated) pass through unchanged, per the
 * resolver's own gate.
 */
const resolveSide = (
  modelType: string,
  featureId: string | undefined,
  name: string | undefined,
  channel: string | undefined,
  platform: string | undefined,
  tactic: string | undefined,
  brandNonbrand: string | undefined,
  featureMaps: FeatureMapsByModelType | undefined
): ResolvedSide => {
  const resolved = resolveFeatureRowMultiMap(
    {
      name,
      feature_id: featureId,
      channel,
      platform,
      tactic,
      brand_nonbrand: brandNonbrand
    },
    () => toFeatureMappingType(modelType),
    featureMaps
  );
  return {
    name: resolved.name || featureId || 'Unknown',
    channel: resolved.channel || '',
    platform: resolved.platform || '',
    tactic: resolved.tactic || '',
    brand_nonbrand: resolved.brand_nonbrand || ''
  };
};

const toNumber = (value: number | string | undefined | null): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Real reallocation-graph rows for the Sankey — parses, filters to mean-estimate rows on enabled
 * model types, groups across models per date/primary/secondary-feature pairing (max(spend),
 * sum(effect) — DEV-21662/Econ's spec, mirroring the maxSpend/totalEffect pattern already used by
 * transformActualizedDataToTwoStage and adaptMddRowsToRoasData in this codebase), and resolves
 * feature ids to display names via the shared adapter.
 */
export const transformTwostageDecompositionRows = (
  rows: TwostageDecompositionCsvRow[],
  enabledModelTypes: string[],
  featureMaps?: FeatureMapsByModelType
): TwostageDecompositionRow[] => {
  if (!rows || rows.length === 0 || enabledModelTypes.length === 0) {
    return [];
  }

  const filtered = rows.filter(
    row => row.estimate_type === METRIC_MEAN && enabledModelTypes.includes(row.slotId)
  );

  const groups = new Map<string, TwostageDecompositionCsvRow[]>();
  filtered.forEach(row => {
    // primary_name/secondary_name are included alongside feature_id because several distinct
    // synthetic entities (e.g. "Covariates" and "Media-Driven Brand Equity", DEV-23906) share
    // feature_id=null -- without the name in the key, their same-date "direct" rows collide into
    // one group and one of them silently absorbs the other's effect.
    const key = [
      row.date,
      row.primary_feature_id,
      row.primary_name,
      row.secondary_feature_id,
      row.secondary_name,
      row.category_type
    ].join(GROUP_KEY_SEPARATOR);
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  });

  const output: TwostageDecompositionRow[] = [];

  groups.forEach(groupRows => {
    if (groupRows.length === 0) {
      return;
    }

    const spendRows = groupRows.filter(r => r.spend_or_effect === 'spend');
    const effectRows = groupRows.filter(r => r.spend_or_effect === 'effect');
    if (spendRows.length === 0 && effectRows.length === 0) {
      return;
    }

    const maxSpend = spendRows.reduce((max, r) => Math.max(max, toNumber(r.value)), 0);
    const totalEffect = effectRows.reduce((sum, r) => sum + toNumber(r.value), 0);

    const first = groupRows[0];
    const categoryType = (first.category_type as TwostageDecompositionRow['cat']) || 'direct';
    const isDirect = categoryType === TWOSTAGE_CATEGORY_DIRECT;
    // Both sides of a decomposition row come from the same source row, so they share one
    // model_type — each resolveSide call dispatches through the per-model-type map for
    // first.model_type.
    const { model_type: modelType } = first;

    const primary = resolveSide(
      modelType,
      first.primary_feature_id,
      first.primary_name,
      first.primary_channel,
      first.primary_platform,
      first.primary_tactic,
      first.primary_brand_nonbrand,
      featureMaps
    );
    // `secondary_name === 'direct'` is a placeholder meaning "no reassignment" — treat direct rows
    // as a self-loop onto the primary feature rather than resolving the literal string "direct".
    const secondary = isDirect
      ? primary
      : resolveSide(
          modelType,
          first.secondary_feature_id,
          first.secondary_name,
          first.secondary_channel,
          first.secondary_platform,
          first.secondary_tactic,
          first.secondary_brand_nonbrand,
          featureMaps
        );

    const primaryMediaType = groupRows.map(r => r.primary_media_type).find(v => v) || undefined;
    const secondaryMediaType = isDirect
      ? primaryMediaType
      : groupRows.map(r => r.secondary_media_type).find(v => v) || undefined;

    const base = {
      date: first.date,
      primary_channel: primary.name,
      primary_channel_feature_id: first.primary_feature_id || undefined,
      primary_channel_channel: primary.channel || undefined,
      primary_channel_platform: primary.platform || undefined,
      primary_channel_tactic: primary.tactic || undefined,
      primary_channel_brand_nonbrand: primary.brand_nonbrand || undefined,
      primary_channel_media_type: primaryMediaType,
      secondary_channel: secondary.name,
      secondary_channel_feature_id:
        (isDirect ? first.primary_feature_id : first.secondary_feature_id) || undefined,
      secondary_channel_channel: secondary.channel || undefined,
      secondary_channel_platform: secondary.platform || undefined,
      secondary_channel_tactic: secondary.tactic || undefined,
      secondary_channel_brand_nonbrand: secondary.brand_nonbrand || undefined,
      secondary_channel_media_type: secondaryMediaType,
      cat: categoryType,
      primary_channel_incrementality_test: false
    };

    if (spendRows.length > 0) {
      output.push({ ...base, value: maxSpend, type: STAGE_TWO_TYPE_SPEND });
    }
    if (effectRows.length > 0) {
      output.push({ ...base, value: totalEffect, type: STAGE_TWO_TYPE_EFFECT });
    }
  });

  return output;
};

const HROAS_QUERY_KEY_PREFIX = 'hroas';

/**
 * Shared fetch for the twostage_decomposition_over_time.csv include. Reads through
 * TWOSTAGE_DECOMPOSITION_INCLUDE / TWOSTAGE_DECOMPOSITION_DATA_KEY (hroasResults.ts) so the
 * provisional `include`/response-key strings only need updating in one place once the sibling
 * bpm-python-lambdas PR (DEV-21662) lands its actual names.
 */
const fetchTwostageDecompositionBundle = (
  company: string,
  kpi: string,
  runId: string | null
): Promise<HroasResultsBundle> =>
  fetchHroasResults(company, kpi, TWOSTAGE_DECOMPOSITION_INCLUDE, runId);

const twostageDecompositionRows = (bundle: HroasResultsBundle): TwostageDecompositionCsvRow[] =>
  tagRowsWithSlot(
    datasetRows(bundle.data[TWOSTAGE_DECOMPOSITION_DATA_KEY] ?? null) as unknown as Omit<
      TwostageDecompositionCsvRow,
      'slotId'
    >[],
    deriveSlots(bundle.models)
  );

export const getTwostageDecompositionQueryKey = (
  company: string,
  kpi: string,
  runId: string | null
): readonly unknown[] => [
  HROAS_QUERY_KEY_PREFIX,
  'twostageDecomposition',
  ...getHroasResultsQueryKey(company, kpi, TWOSTAGE_DECOMPOSITION_INCLUDE, runId)
];

/**
 * Fetch + transform for the Sankey's real reallocation data, also consumed by the CPTB
 * "Actualized" bar chart (the same underlying data).
 */
export const useTwostageDecompositionData = (
  company: string,
  kpi: string,
  runId: string | null,
  enabledModelTypes: string[],
  featureMaps?: FeatureMapsByModelType
): UseQueryResult<TwostageDecompositionRow[], unknown> => {
  const select = useCallback(
    (bundle: HroasResultsBundle) =>
      transformTwostageDecompositionRows(
        twostageDecompositionRows(bundle),
        enabledModelTypes,
        featureMaps
      ),
    [enabledModelTypes, featureMaps]
  );
  return useQuery({
    queryKey: getTwostageDecompositionQueryKey(company, kpi, runId),
    queryFn: () => fetchTwostageDecompositionBundle(company, kpi, runId),
    select,
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0
  });
};
